import { createContext, PropsWithChildren, RefObject, useContext, useEffect, useRef, useState } from "react"

export enum ResponsiveSize {
  xs,
  sm,
  md, 
  lg, 
  xl,
  xxl
}

interface PlayResponsiveContextState {
  xs: boolean
  sm: boolean
  md: boolean
  lg: boolean
  xl: boolean
  xxl: boolean
  size: ResponsiveSize
  isPortraitMode: boolean
}

const PlayResponsiveContext = createContext<PlayResponsiveContextState>({
  xs: false,
  sm: false,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  size: ResponsiveSize.xs,
  isPortraitMode: false,
})

export function usePlayResponsive() {
  return useContext(PlayResponsiveContext);
}

function isVisible(ref: RefObject<HTMLDivElement>) {
  let isVisible = false;
  if (ref.current) {
    const style = window.getComputedStyle(ref.current)
    isVisible = style.display !== "none" && style.visibility === "visible";
  }
  return isVisible
}

export function PlayResponsiveContextProvider(props: PropsWithChildren<{}>) {
  const {children} = props;
  const xsRef = useRef<HTMLDivElement>(null);
  const smRef = useRef<HTMLDivElement>(null);
  const mdRef = useRef<HTMLDivElement>(null);
  const lgRef = useRef<HTMLDivElement>(null);
  const xlRef = useRef<HTMLDivElement>(null);
  const xxlRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<PlayResponsiveContextState>({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxl: false,
    size: ResponsiveSize.xs,
    isPortraitMode: false
  })

  useEffect(() => {
    function updateState() {
      const rt = document.documentElement.getBoundingClientRect();

      const xs = isVisible(xsRef)
      const sm = isVisible(smRef)
      const md = isVisible(mdRef)
      const lg = isVisible(lgRef)
      const xl = isVisible(xlRef)
      const xxl = isVisible(xxlRef) 
      
      let size: ResponsiveSize = ResponsiveSize.xs
      if (xs) size = ResponsiveSize.xs
      if (sm) size = ResponsiveSize.sm
      if (md) size = ResponsiveSize.md
      if (lg) size = ResponsiveSize.lg
      if (xl) size = ResponsiveSize.xl
      if (xxl) size = ResponsiveSize.xxl
      
      const newState:PlayResponsiveContextState = {
        xs,
        sm,
        md,
        lg,
        xl,
        xxl,
        size,
        isPortraitMode: rt.width < rt.height
      }
      // console.log(newState)
      setState(newState)
    }

    updateState()
    window.addEventListener("resize", updateState)
    return () => {
      window.removeEventListener("resize", updateState)
    }
  }, [])

  return <PlayResponsiveContext.Provider value={state}>
    <div className="play-responsive-context">
      <div className="d-block d-sm-none" ref={xsRef} />
      <div className="d-none d-sm-block d-md-none" ref={smRef} />
      <div className="d-none d-md-block d-lg-none" ref={mdRef} />
      <div className="d-none d-lg-block d-xl-none" ref={lgRef} />
      <div className="d-none d-xl-block d-xlx-none" ref={xlRef} />
      <div className="d-none d-xxl-block" ref={xxlRef} />  
    </div>
    {
      xsRef.current && smRef.current && mdRef.current && lgRef.current && xlRef.current && xxlRef.current &&
      children
    }
  </PlayResponsiveContext.Provider>
}